import * as E from 'fp-ts/Either';
import {
  Box,
  Button,
  JsonInput,
  TagsInput,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { App, insert, update } from '../api/apps';
import { ChangeEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';

type AppFormData = {
  friendlyName: string;
  shortCode: string;
  requiredLicenseFields: string[];
  sharedLicenseData: Record<string, any>;
  sharedLicenseDataString: string;
  validLicenseData: boolean;
};

export const AppForm = ({ app }: { app: App | null }) => {
  const [disableSensitiveFields, setDisableSensitiveFields] = useState(!!app);
  const auth = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AppFormData>({
    friendlyName: app?.friendlyName ?? '',
    shortCode: app?.shortCode ?? '',
    requiredLicenseFields: app?.requiredLicenseFields ?? [],
    sharedLicenseData: app?.sharedLicenseData ?? {},
    sharedLicenseDataString: JSON.stringify(
      app?.sharedLicenseData ?? {},
      null,
      2
    ),
    validLicenseData: true,
  });

  const [tagInput, setTagInput] = useState('');

  const handleChange =
    (k: keyof AppFormData) => (e: ChangeEvent<HTMLInputElement>) =>
      setFormData({ ...formData, [k]: e.target.value });

  const licenseDataChanged = (value: string) => {
    try {
      const data = JSON.parse(value);
      setFormData({
        ...formData,
        sharedLicenseDataString: value,
        sharedLicenseData: data,
        validLicenseData: true,
      });
    } catch (e) {
      setFormData({
        ...formData,
        sharedLicenseDataString: value,
        sharedLicenseData: {},
        validLicenseData: false,
      });
      return;
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!auth?.token) {
      return;
    }
    if (!formData.shortCode || !formData.friendlyName) {
      toast.error('Please fill out all fields');
      return;
    }
    if (!formData.validLicenseData) {
      toast.error('Please ensure shared license data is valid');
      return;
    }

    const pendingTags = tagInput.trim()
      ? [...formData.requiredLicenseFields, tagInput.trim()]
      : formData.requiredLicenseFields;

    const record = {
      friendlyName: formData.friendlyName,
      shortCode: formData.shortCode,
      requiredLicenseFields: pendingTags,
      sharedLicenseData: formData.sharedLicenseData,
    };

    const result = app
      ? await update(auth.token, app.shortCode, record)
      : await insert(auth.token, record);

    if (E.isRight(result)) {
      toast.success(app ? 'App updated' : 'App created');
      navigate(`/apps/${result.right.shortCode}`, { replace: true });
    } else {
      toast.error(result.left.message);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <TextInput
        label="Name"
        name="friendlyName"
        value={formData.friendlyName}
        onChange={handleChange('friendlyName')}
        required
      />
      <Box mt="lg" mb="lg">
        <Title order={4} mb="md">
          Technical implementation
        </Title>

        <Text mb="md">
          These fields are used to match a license to an installation. For
          example you may use it to match a license to a client or a particular
          vat registration number.{' '}
          {app ? (
            <strong>
              Once the application is live they should not be changed.
            </strong>
          ) : null}
        </Text>
        {disableSensitiveFields ? (
          <Button
            variant="outline"
            my="md"
            color="red"
            onClick={(e) => {
              e.preventDefault();
              setDisableSensitiveFields(false);
            }}
          >
            Edit sensitive data
          </Button>
        ) : null}
        <TextInput
          label="Short code"
          name="shortCode"
          value={formData.shortCode}
          onChange={handleChange('shortCode')}
          disabled={disableSensitiveFields}
          required
        />
        <TagsInput
          label="Required license fields"
          description="A list of field tags that will be used to match a license to an installation. e.g. client ,  vrn. To add a tag, type the name and press enter"
          value={formData.requiredLicenseFields}
          onChange={(value) =>
            setFormData({ ...formData, requiredLicenseFields: value })
          }
          onSearchChange={setTagInput}
          splitChars={[',', ' ']}
          disabled={disableSensitiveFields}
        />
        <JsonInput
          label="Shared license data"
          description="This data will be returned on every license check for every license."
          value={formData.sharedLicenseDataString}
          onChange={licenseDataChanged}
          disabled={disableSensitiveFields}
        />
      </Box>
      <Button type="submit" mt="md">
        {app ? 'Save changes' : 'Create app'}
      </Button>
    </form>
  );
};
